<template>
  <!-- Класс для этого элемента не применяется (видимо где-то затирается) -->
  <div>
    <div class="table__container">
      <button
        v-if="isShowButtons"
        class="table__button table__button--left"
        :disabled="isLeftBtnDisabled"
        aria-label="Листать назад"
        @click="applyScroll('prev')"
        @mousedown="startAutoScroll('prev')"
        @mouseup="stopScroll"
        @mouseleave="stopScroll"
      />
      <div
        ref="tableWrapp"
        class="table__wrapp"
        @scroll="updateScrollLeft"
      >
        <table class="table">
          <slot />
        </table>
      </div>
      <button
        v-if="isShowButtons"
        class="table__button table__button--right"
        :disabled="isRightBtnDisabled"
        aria-label="Листать вперёд"
        @click="applyScroll('next')"
        @mousedown="startAutoScroll('next')"
        @mouseup="stopScroll"
        @mouseleave="stopScroll"
      />
    </div>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import { useDebounceFn } from '@vueuse/core';

  export default defineNuxtComponent({
    name: 'TheTable',
    props: {
      autoScrollStep: {
        type: Number as PropType<number>,
        default: 20,
      },
      autoScrollStartDelay: {
        type: Number as PropType<number>,
        default: 300,
      },
      autoScrollDelay: {
        type: Number as PropType<number>,
        default: 100,
      },
    },

    setup() {
      // TODO: check window resize
      const { width, height } = useWindowSize();
      return {
        windowHeight: height,
        windowWidth: width,
      };
    },
    data() {
      return {
        isShowButtons: false as boolean,
        table: null as HTMLElement | null,
        delayAutoScrollTimer: null as number | null,
        autoScrollTimer: null as number | null,
        scrollLeft: 0 as number,
      };
    },

    computed: {
      isLeftBtnDisabled(): boolean {
        if (this.table) {
          return this.scrollLeft <= this.table?.offsetWidth;
        }
        return true;
      },
      isRightBtnDisabled(): boolean {
        if (this.table) {
          return this.scrollLeft >= this.table?.scrollWidth;
        }
        return true;
      },
    },

    watch: {
      windowWidth() {
        this.resizeHandler();
      },
    },
    mounted() {
      this.table = this.$refs.tableWrapp as HTMLElement;
      this.isShowButtons =
        this.table?.offsetWidth < this.table?.scrollWidth && this.windowWidth >= 991;
      this.scrollLeft = this.table?.offsetWidth;
    },

    methods: {
      updateScrollLeft: useDebounceFn(function (this: any) {
        const scrollWidth = this.table?.scrollWidth || 0;
        const offsetWidth = this.table?.offsetWidth || 0;
        this.scrollLeft = this.table?.scrollLeft + offsetWidth;

        if (this.scrollLeft <= offsetWidth || this.scrollLeft >= scrollWidth) {
          this.scrollLeft = this.scrollLeft <= offsetWidth ? offsetWidth : scrollWidth;
          this.stopScroll();
        }
      }, 150),
      resizeHandler: useDebounceFn(function (this: any) {
        this.isShowButtons = this.windowWidth >= 991;
      }, 300),
      startAutoScroll(direction: string): void {
        this.delayAutoScrollTimer = window.setTimeout(() => {
          this.autoScrollTimer = window.setInterval(() => {
            this.scrollTo(this.autoScrollStep, direction);
          }, this.autoScrollDelay);
        }, this.autoScrollStartDelay);
      },
      stopScroll(): void {
        window.setTimeout(() => {
          if (this.autoScrollTimer !== null) {
            window.clearInterval(this.autoScrollTimer);
          }
          if (this.delayAutoScrollTimer !== null) {
            window.clearTimeout(this.delayAutoScrollTimer);
          }
          this.autoScrollTimer = null;
          this.delayAutoScrollTimer = null;
        });
      },
      applyScroll(direction: string): void {
        if (!this.autoScrollTimer) {
          this.scrollTo(this.table?.offsetWidth || 0, direction);
        }
      },
      scrollTo(scrollStep: number, direction: string): void {
        scrollStep = direction === 'next' ? scrollStep : -scrollStep;
        this.table?.scrollBy(scrollStep, 0);
      },
    },
  });
</script>

<style lang="scss">
  @use '@devhacker/shared/styles/scrollbar.scss' as scrollbar;

  // Костыль для страницы "Редакция" https://lifehacker.ru/team,
  // убираем стили таблицы для отображения в столбцах
  .editor-additional-class__team-page-table table {
    border: none;

    td {
      width: 50%;
      padding: 0;
      @include fontNormalText;
    }
  }

  .table__wrapp {
    margin: 24px 0;
    overflow: hidden;
    overflow-x: auto;
    scroll-behavior: smooth;

    @include scrollbar.customScrollbar;
  }

  // Отдельный стиль для таблиц внутри обертки которые приходят из апи
  // Не тоже самое что .table класс
  table {
    min-width: 100%;
    border: 1px solid #e7e7e7;
    border-top: 3px solid var(--main-color);
    border-collapse: collapse;

    tr:nth-child(even) {
      background-color: rgb(246, 246, 246);
    }

    tr {
      @include fontSmallText;
    }

    td {
      @include fontSmallText;
      padding: 12px 16px;
      vertical-align: top;
    }
  }

  .table {
    position: relative;

    &__container:hover .table__button {
      opacity: 1;
    }

    &__button {
      box-shadow: 0 3px 10px rgba(0, 0, 0, 0.25);
      border: none;
      border-radius: 50%;
      width: 32px;
      height: 32px;
      background-color: #ffffff;
      position: absolute;
      top: 50%;
      transition: opacity 0.2s linear;
      opacity: 0;
      z-index: 1;

      &::before {
        content: '';
        display: block;
        width: 100%;
        height: 100%;
        mask-image: url('@/assets/images/arrow-right.svg');
        background-color: #969698;
        transition: background-color 0.2s linear;
      }

      &:hover {
        &::before {
          background-color: #000000;
        }
      }

      &:not(:disabled) {
        cursor: pointer;
      }

      &.table__button:disabled {
        pointer-events: none;
        opacity: 0;
      }

      &--left {
        left: 18px;
        transform: scaleX(-1) translateY(-50%);
      }

      &--right {
        right: 18px;
        transform: translateY(-50%);
      }
    }
  }
</style>
